import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbortError } from '../lib/api';
import { usePageVisible } from './usePageVisible';

export interface ResourceOptions<T> {
  /** Keep re-fetching every `pollIntervalMs` while this returns true (and the tab is visible). */
  pollWhile?: (data: T) => boolean;
  pollIntervalMs?: number;
}

export interface Resource<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
  /** Replaces the loaded data locally (e.g. with a mutation's response). */
  setData: (data: T) => void;
}

interface State<T> {
  key: string;
  data: T | null;
  error: unknown;
  loading: boolean;
}

/**
 * Loads data identified by `key`, cancelling stale requests. Polling reloads
 * keep showing the previous data instead of flashing a loading state.
 */
export function useResource<T>(
  key: string,
  load: (signal: AbortSignal) => Promise<T>,
  { pollWhile, pollIntervalMs = 3000 }: ResourceOptions<T> = {},
): Resource<T> {
  const [state, setState] = useState<State<T>>({ key, data: null, error: null, loading: true });
  const [reloadToken, setReloadToken] = useState(0);
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });
  const visible = usePageVisible();
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    inFlight.current = controller;
    setState((current) =>
      current.key === key ? { ...current, loading: true } : { key, data: null, error: null, loading: true },
    );
    loadRef.current(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ key, data, error: null, loading: false });
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setState((current) => ({ ...current, key, error, loading: false }));
      },
    );
    return () => controller.abort();
  }, [key, reloadToken]);

  const current = state.key === key ? state : { key, data: null, error: null, loading: true };
  const shouldPoll = visible && current.data !== null && pollWhile !== undefined && pollWhile(current.data);

  useEffect(() => {
    if (!shouldPoll) return;
    const timer = window.setInterval(() => setReloadToken((n) => n + 1), pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [shouldPoll, pollIntervalMs]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  // Drops any load (e.g. a poll) already in flight: it may have read the data from before the mutation.
  const setData = useCallback(
    (data: T) => {
      inFlight.current?.abort();
      setState({ key, data, error: null, loading: false });
    },
    [key],
  );

  return { data: current.data, error: current.error, loading: current.loading, reload, setData };
}
